import {
  friendLoungeResponseSchema,
  guestLinkStatusResponseSchema,
  guestSeatResponseSchema,
  mintGuestLinkResponseSchema,
  type FriendLoungeResponse,
  type GameKind,
  type GuestLinkStatusResponse,
  type GuestSeatResponse,
  type MintGuestLinkResponse,
} from '@awwyeah/shared';

/**
 * Client half of the guest play-link REST surface (WS-23). Same shape as
 * `modules/auth/api.ts`: `credentials: 'include'` everywhere, every successful body
 * zod-parsed at the boundary, non-2xx turned into a typed error rather than a silent
 * `undefined`.
 *
 * The public reads (`apiReadGuestSeat`, `apiClaimGuestSeat`) map the server's uniform `404`
 * onto `null` instead of throwing: to the page, "no such link" is a state to render, not an
 * exception — and it deliberately looks the same as every other way a token can be wrong.
 */

export class GuestLinkApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(code);
    this.name = 'GuestLinkApiError';
  }
}

async function readErrorCode(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    return data.error ?? `http_${res.status}`;
  } catch {
    return `http_${res.status}`;
  }
}

async function expectOk<T>(res: Response, parse: (data: unknown) => T): Promise<T> {
  if (!res.ok) throw new GuestLinkApiError(res.status, await readErrorCode(res));
  return parse(await res.json());
}

/** Mints (or regenerates) the link for a game. Returns the raw token exactly once. */
export async function apiMintGuestLink(gameId: string): Promise<MintGuestLinkResponse> {
  const res = await fetch(`/api/games/${encodeURIComponent(gameId)}/guest-link`, {
    method: 'POST',
    credentials: 'include',
  });
  return expectOk(res, (d) => mintGuestLinkResponseSchema.parse(d));
}

export async function apiGetGuestLinkStatus(gameId: string): Promise<GuestLinkStatusResponse> {
  const res = await fetch(`/api/games/${encodeURIComponent(gameId)}/guest-link`, {
    credentials: 'include',
  });
  return expectOk(res, (d) => guestLinkStatusResponseSchema.parse(d));
}

export async function apiRevokeGuestLink(gameId: string): Promise<void> {
  await fetch(`/api/games/${encodeURIComponent(gameId)}/guest-link`, {
    method: 'DELETE',
    credentials: 'include',
  });
}

/** Public. `null` = this link buys nothing, for any reason at all. */
export async function apiReadGuestSeat(token: string): Promise<GuestSeatResponse | null> {
  const res = await fetch(`/api/games/play/${encodeURIComponent(token)}`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  return expectOk(res, (d) => guestSeatResponseSchema.parse(d));
}

/**
 * Public. Takes the seat and, on success, the server sets the guest session cookie. A `409`
 * means somebody else got there first — a real answer ("taken"), not an error to throw.
 */
export async function apiClaimGuestSeat(
  token: string,
  name?: string,
): Promise<GuestSeatResponse | null> {
  const res = await fetch(`/api/games/play/${encodeURIComponent(token)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(name ? { name } : {}),
  });
  if (res.status === 404) return null;
  if (res.status === 409) return { state: 'taken' };
  return expectOk(res, (d) => guestSeatResponseSchema.parse(d));
}

/* -------------------------------------------------------------------------- */
/* Friend of the family                                                        */
/* -------------------------------------------------------------------------- */

/** The lounge. `null` = this browser holds no friend pass (never claimed a seat here). */
export async function apiReadLounge(): Promise<FriendLoungeResponse | null> {
  const res = await fetch('/api/games/friend', { credentials: 'include' });
  if (res.status === 401) return null;
  return expectOk(res, (d) => friendLoungeResponseSchema.parse(d));
}

/** One of the friend's matches, in the claimed-link shape. `null` = not theirs / not here. */
export async function apiReadFriendGame(gameId: string): Promise<GuestSeatResponse | null> {
  const res = await fetch(`/api/games/friend/games/${encodeURIComponent(gameId)}`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  return expectOk(res, (d) => guestSeatResponseSchema.parse(d));
}

/** "Start another game with Dan." Throws `GuestLinkApiError` (`too_many`, …) on refusal. */
export async function apiStartFriendGame(
  kind: GameKind,
  memberId: string,
): Promise<GuestSeatResponse> {
  const res = await fetch('/api/games/friend/games', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ kind, memberId }),
  });
  return expectOk(res, (d) => guestSeatResponseSchema.parse(d));
}

/**
 * Member side: after a Rematch against a guest seat, tell the server that friend may open
 * the new match. Best-effort — if it fails the rematch still exists for the member, the
 * friend just won't see it in their lounge.
 */
export async function apiAttachGuestSeat(gameId: string, seatId: string): Promise<void> {
  try {
    await fetch(`/api/games/${encodeURIComponent(gameId)}/guest-seat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ seatId }),
    });
  } catch {
    // Offline: nothing to surface.
  }
}
